/**
 * La vignette d'un article : la sienne, ou celle qu'on dessine à sa place.
 *
 * Un article sans `cover` laissait un rectangle vide partout : dans la liste des
 * articles, dans les résultats de recherche Algolia, et sur les réseaux sociaux.
 * Ces trois endroits lisaient le même champ vide.
 *
 * **On ne stocke jamais l'image générée, seulement son adresse.** C'est le
 * choix structurant, et il évite trois ennuis :
 *
 * - `cover` garde son sens. Rempli veut dire « quelqu'un a choisi cette
 *   image ». Y écrire une valeur générée rendrait impossible de distinguer une
 *   vignette voulue d'une vignette par défaut, et donc de savoir laquelle
 *   remplacer.
 * - Rien ne se périme. L'image se redessine à partir du titre et de la
 *   description du moment ; un titre corrigé donne une vignette corrigée, sans
 *   régénérer ni re-téléverser quoi que ce soit.
 * - L'effet est rétroactif. Les articles déjà en base l'ont sans traitement de
 *   rattrapage.
 *
 * Deux formes, parce que les usages diffèrent : un chemin relatif suffit dans
 * l'application, une adresse absolue est indispensable pour Algolia et pour les
 * balises Open Graph, qui sont lues hors du site.
 */

/**
 * Chemin relatif de la miniature. Utilisable côté navigateur.
 *
 * Pointe sur /api/thumbnail/article, distinct de /api/og/article : la carte de
 * partage est vue seule et en grand, la miniature est vue en grille et petite.
 * Elles ne portent donc pas le même dessin.
 */
export function articleCoverPath(article, locale = "fr") {
    const cover = (article?.cover || "").trim();
    if (cover) return cover;

    const slug = article?.slug;
    if (!slug) return "";

    // Numéro de version : l'horodatage du dernier réglage de miniature.
    //
    // Sans lui, régler une vignette ne se voit pas. Trois caches se superposent
    // sur ce chemin : la réponse image (une heure), l'appel au back qui la
    // nourrit (une heure), et /_next/image par-dessus. Comme l'adresse ne
    // dépendait que du slug et de la langue, aucun des trois n'avait de raison
    // de se renouveler, et l'administrateur voyait son ancienne miniature en
    // croyant à un bug.
    //
    // C'est aussi ce paramètre que la route fait suivre jusqu'au back, pour que
    // le cache de l'appel de données se renouvelle avec les autres.
    //
    // Limite assumée : effacer les réglages d'un article fait disparaître `maj`
    // avec eux, donc l'adresse redevient celle d'avant, sans version. C'est la
    // bonne adresse (le rendu attendu est de nouveau le gabarit historique),
    // mais si quelqu'un l'a demandée pendant que l'article était réglé, son
    // cache peut encore porter la vignette réglée pendant une heure. Le cas est
    // rare, et le tenir demanderait de garder un horodatage sur une colonne
    // qu'on vient précisément de vider.
    const maj = Number(article?.thumbnailSettings?.maj) || 0;
    const version = maj ? `&v=${maj}` : "";

    return `/api/thumbnail/article?slug=${encodeURIComponent(slug)}&locale=${
        locale === "en" ? "en" : "fr"
    }${version}`;
}

/**
 * Adresse absolue de la vignette. Pour Algolia et les métadonnées, qui sont
 * lues depuis l'extérieur du site.
 */
export function articleCoverUrl(article, locale = "fr") {
    const chemin = articleCoverPath(article, locale);
    if (!chemin || chemin.startsWith("http")) return chemin;

    const base = process.env.NEXTAUTH_URL || "https://believemy.com";
    return `${base}${chemin}`;
}
